<script setup>
import { computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import ButtonNext from 'next/button/Button.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// VOXTELL (manifest Cat-4, "Profile menu account switcher"): the org lives with the person.
// The trigger's second line shows the current account; switching moves into this menu
// (Intercom's pattern) so the sidebar header can be brand-only.
const { currentAccount } = useAccount();
const userAccounts = useMapGetter('getUserAccounts');
const showAccountSwitcher = computed(() => userAccounts.value.length > 1);
const sortedCurrentUserAccounts = computed(() =>
  [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);
const onChangeAccount = newId => {
  window.location.href = `/app/accounts/${newId}/dashboard`;
};

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const toggleChatSupport = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: toggleChatSupport,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div v-if="!isCollapsed" class="min-w-0 flex-grow">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentAccount?.name || currentUser.email }}
          </div>
        </div>
        <Icon
          v-if="!isCollapsed && showAccountSwitcher"
          icon="i-lucide-chevrons-up-down"
          class="flex-shrink-0 size-4 text-n-slate-10"
        />
      </button>
    </template>
    <DropdownBody class="bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0">
      <template v-if="showAccountSwitcher">
        <DropdownSection
          :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')"
          height="max-h-52"
        >
          <DropdownItem
            v-for="account in sortedCurrentUserAccounts"
            :id="`account-${account.id}`"
            :key="account.id"
            class="cursor-pointer"
            @click="onChangeAccount(account.id)"
          >
            <template #label>
              <div class="flex gap-2 items-center text-left rtl:text-right">
                <span
                  class="text-n-slate-12 max-w-36 truncate min-w-0"
                  :title="account.name"
                >
                  {{ account.name }}
                </span>
                <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
                <span class="text-n-slate-11 max-w-24 truncate capitalize">
                  {{
                    account.custom_role_id
                      ? account.custom_role.name
                      : account.role
                  }}
                </span>
              </div>
              <Icon
                v-show="account.id === accountId"
                icon="i-lucide-check"
                class="text-n-teal-11 size-5"
              />
            </template>
          </DropdownItem>
        </DropdownSection>
        <DropdownItem v-if="globalConfig.createNewAccountFromDashboard">
          <ButtonNext
            color="slate"
            variant="faded"
            class="w-full"
            size="sm"
            @click="emit('showCreateAccountModal')"
          >
            {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
          </ButtonNext>
        </DropdownItem>
        <DropdownSeparator />
      </template>
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
